import { clearReplica } from './db';
import { unsyncedOpCount } from './opQueue';

/**
 * Wipe the local PHI replica when a session ends by a NON-logout path — token
 * expiry/revocation, cross-tab sign-out, the Firebase `onAuthStateChanged(null)`
 * callback. PHI must not outlive the session on a shared device, BUT this path
 * must not be more destructive than the explicit Logout button, which counts
 * un-synced ops and confirms before discarding them (see useAuth.logout).
 *
 * The auth callback can't show a confirm dialog (it may fire in a background
 * tab, mid-redirect, etc.), so it achieves the same "no silent data loss"
 * guarantee by PRESERVING the replica + op-queue whenever un-synced writes
 * exist — the stranded-offline vet whose token expired keeps their unsynced
 * clinical work instead of losing it. The preserved queue is only ever drained
 * by the SAME user resuming: syncClient.pull() wipes it first on a clinic OR
 * user switch, so a different vet on a shared device can neither push under the
 * wrong tenant nor be mis-attributed as the author of another vet's offline
 * writes (a different-user sign-in therefore discards the preserved work — no
 * worse than the pre-B3 unconditional wipe, and the safer choice vs wrong
 * clinical authorship on legally-retained PHI).
 *
 * A5 (shipped) adds a cached-token grace window in AuthProvider so a
 * non-intentional auth-null arriving WHILE OFFLINE keeps the cached session
 * mounted instead of stranding the vet — see auth/offlineAuthGrace.ts. (Note:
 * a purely-offline token expiry does NOT itself fire onAuthStateChanged(null);
 * Firebase keeps the user and only emits null on a server-verdict revocation
 * (online), a cross-tab signOut, or persistence eviction.) Still tracked
 * SEPARATELY, NOT delivered by A5: local unlock (PIN/WebAuthn) and per-op author
 * identity (B13) so a preserved queue can sync attributed to its real author.
 * If the queue can't be read, fail safe to a wipe — matching the Logout button,
 * which would rather end the session than strand PHI on a shared device.
 */
export async function wipeReplicaOnSessionEnd(): Promise<void> {
  let unsynced = 0;
  try {
    unsynced = await unsyncedOpCount();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Offline unsynced-op count on session end failed; wiping:', e);
  }

  if (unsynced > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `Session ended with ${unsynced} un-synced offline change(s); preserving ` +
        `the local queue to avoid silent data loss (offline-auth grace = A5).`,
    );
    return;
  }

  try {
    await clearReplica();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Offline replica clear on session end failed:', e);
  }
}
